import _ from 'lodash'
import { buildWidgetUrl } from './resolveRemoteRefs'
import {
	pointers,
	Component,
	Structure,
	Data,
	QueryProperty,
	ConnectionList,
	IConnectionItem,
	ControllerConnectionItem,
	ConnectionType,
	ActionBehavior,
	ObsoleteBehaviorsList,
} from '@wix/thunderbolt-becky-types'
import { DataMapName, InflatedByRefComp, InflatedByRefStructure, RefData, ResolvedRef } from '../types/byRef'

const DATA_MAPS = pointers.data.DATA_MAPS
const PROPERTY_TYPES = pointers.components.PROPERTY_TYPES
const QUERY_TO_MAP_NAME = pointers.QUERY_TO_MAP_NAME

const REF_DELIMITER = '_r_'

const DATA_QUERIES_TO_INFLATE = [
	PROPERTY_TYPES.BEHAVIOR_QUERY,
	PROPERTY_TYPES.BREAKPOINTS_QUERY,
	PROPERTY_TYPES.PROPERTY_QUERY,
	PROPERTY_TYPES.CONNECTION_QUERY,
	PROPERTY_TYPES.DESIGN_QUERY,
	PROPERTY_TYPES.DATA_QUERY,
	PROPERTY_TYPES.LAYOUT_QUERY,
	PROPERTY_TYPES.MOBILE_HINTS_QUERY,
	PROPERTY_TYPES.STYLE_ID,
	PROPERTY_TYPES.TRANSFORMATION_QUERY,
	PROPERTY_TYPES.TRANSITION_QUERY,
]

const resolveInternalRefs = (rootCompId: string, masterPageStructure: Structure, masterPageData: Data): ResolvedRef => {
	const structureResult: any /* Structure */ = {}

	// TODO: fix definition for data
	const dataResult: RefData = {
		[DATA_MAPS.BEHAVIORS]: {},
		[DATA_MAPS.BREAKPOINTS]: {},
		[DATA_MAPS.PROPERTIES]: {},
		[DATA_MAPS.CONNECTIONS]: {},
		[DATA_MAPS.DESIGN]: {},
		[DATA_MAPS.DATA]: {},
		[DATA_MAPS.LAYOUT]: {},
		[DATA_MAPS.MOBILE_HINTS]: {},
		[DATA_MAPS.STYLE]: {},
		[DATA_MAPS.TRANSFORMATIONS]: {},
		[DATA_MAPS.TRANSITIONS]: {},
		[DATA_MAPS.ANCHOR]: {},
		[DATA_MAPS.VARIANTS]: {},
	}

	const resolveAndAddComponentFromStructure = (component: Component) =>
		(structureResult[component.id] = { ...component, templateId: component.id })

	const resolveAndAddComponentDataItems = (component: Component) => {
		Object.keys(QUERY_TO_MAP_NAME).forEach((dataQuery) => {
			const compQuery = component[dataQuery as QueryProperty]
			if (!compQuery) {
				return
			}

			const dataMap = QUERY_TO_MAP_NAME[dataQuery as QueryProperty]
			dataResult[dataMap][compQuery] = {
				...masterPageData[dataMap][compQuery],
			}
		})
	}

	const resolveStructureAndDataRecursively = (component: Component) => {
		const childComponent = resolveAndAddComponentFromStructure(component)
		resolveAndAddComponentDataItems(childComponent)

		if (!component.components || component.components.length === 0) {
			return
		}
		component.components.forEach((childComponentId: string) => {
			const childComp = masterPageStructure[childComponentId]
			resolveStructureAndDataRecursively(childComp)
		})
	}

	const rootComp = masterPageStructure[rootCompId]
	resolveStructureAndDataRecursively(rootComp)

	return { structure: structureResult, data: dataResult, rootId: rootCompId }
}

const getUniqueId = (refId: string, originalId: string) => `${refId}${REF_DELIMITER}${originalId}`

const getUniqueDataQuery = (id: string, suffix: string) => `${id}-${suffix}`

const fixInflatedConnectionItem = (
	connectionItem: ControllerConnectionItem,
	originalStructure: Structure,
	refId: string
) => {
	if (connectionItem.type !== ConnectionType.ConnectionItem || !connectionItem.controllerId) {
		return connectionItem
	}

	const originalComp = Object.values(originalStructure).find(
		(value) => value.dataQuery === connectionItem.controllerId
	)

	if (!originalComp) {
		return connectionItem
	}

	const oldCompUniqueId = getUniqueId(refId, originalComp.id)
	const inflatedId = getUniqueDataQuery(oldCompUniqueId, PROPERTY_TYPES.DATA_QUERY)

	return { ...connectionItem, controllerId: inflatedId }
}

const inflateDataQueries = (compStructure: Component, compUniqueId: string) => {
	return _(DATA_QUERIES_TO_INFLATE)
		.mapKeys((x) => x)
		.mapValues((dataQueryName) =>
			compStructure[dataQueryName] ? getUniqueDataQuery(compUniqueId, dataQueryName) : null
		)
		.pickBy((x) => x)
		.value()
}

const getEmptyDataMaps: () => RefData = () => ({
	[DATA_MAPS.BEHAVIORS]: {},
	[DATA_MAPS.BREAKPOINTS]: {},
	[DATA_MAPS.PROPERTIES]: {},
	[DATA_MAPS.CONNECTIONS]: {},
	[DATA_MAPS.DESIGN]: {},
	[DATA_MAPS.DATA]: {},
	[DATA_MAPS.LAYOUT]: {},
	[DATA_MAPS.MOBILE_HINTS]: {},
	[DATA_MAPS.STYLE]: {},
	[DATA_MAPS.TRANSFORMATIONS]: {},
	[DATA_MAPS.TRANSITIONS]: {},
	[DATA_MAPS.ANCHOR]: {},
	[DATA_MAPS.VARIANTS]: {},
})

const inflateSingleComp = (compStructure: Component, originalCompId: string, refId: string, rootId: string) => {
	const compUniqueId = getUniqueId(refId, originalCompId)
	const isRootComp = originalCompId === rootId

	return {
		...compStructure,
		...inflateDataQueries(compStructure, compUniqueId),
		templateId: originalCompId,
		id: compUniqueId,
		parent: isRootComp ? refId : getUniqueId(refId, compStructure.parent as string),
		components: compStructure.components
			? compStructure.components.map((childCompId) => getUniqueId(refId, childCompId))
			: undefined,
	}
}

const inflateSingleDataItem = (
	dataMapName: DataMapName,
	dataQueryName: QueryProperty,
	comp: InflatedByRefComp,
	originalStructure: Structure,
	originalData: Data | RefData,
	refId: string
) => {
	const inflatedDataQuery = comp[dataQueryName]
	if (!inflatedDataQuery) {
		return
	}
	const originalCompId = comp.templateId
	const originalDataQuery = originalStructure[originalCompId][dataQueryName] as string
	const originalDataItem = originalData[dataMapName][originalDataQuery]
	const inflatedDataItem = { ...originalDataItem, id: inflatedDataQuery }
	if (dataQueryName === 'connectionQuery' && inflatedDataItem.type === 'ConnectionList') {
		// @ts-ignore
		inflatedDataItem.items = inflatedDataItem.items.map((connectionItem) =>
			fixInflatedConnectionItem(connectionItem, originalStructure, refId)
		)
	}

	return inflatedDataItem
}

const inflateRef = (resolvedStructureAndData: ResolvedRef, refId: string): ResolvedRef => {
	const { rootId, structure: originalStructure, data: originalData } = resolvedStructureAndData
	const inflatedStructure: InflatedByRefStructure = _(originalStructure)
		.mapValues((comp, originalCompId) => inflateSingleComp(comp, originalCompId, refId, rootId))
		.mapKeys('id')
		.value()

	const inflatedData: RefData = _.reduce(
		inflatedStructure,
		(acc, comp) => {
			DATA_QUERIES_TO_INFLATE.forEach((dataQueryName) => {
				const dataMapName = QUERY_TO_MAP_NAME[dataQueryName]
				const inflatedDataItem = inflateSingleDataItem(
					dataMapName,
					dataQueryName,
					comp,
					originalStructure,
					originalData,
					refId
				)
				if (inflatedDataItem) {
					acc[dataMapName][inflatedDataItem.id] = inflatedDataItem
				}
			})
			return acc
		},
		getEmptyDataMaps()
	)

	return { structure: inflatedStructure, data: inflatedData, rootId: getUniqueId(refId, rootId) }
}

const mergeInflatedData = (dataArrayToMerge: { [key: string]: ResolvedRef }) =>
	Object.values(dataArrayToMerge).length > 0
		? Object.values(dataArrayToMerge)
				.map((arr) => arr.data)
				.reduce((prev, curr) => _.merge(prev, curr), {})
		: {}

const mergeData = (original: Data, resolved: Data) => _.merge({}, resolved, original)

const fixDataAfterInflate = (pageData: Data, originalCompIdToInflatedCompIdMap: { [key: string]: string }) => {
	const fixBehaviourItem = (behaviourItem: ActionBehavior) => {
		if (behaviourItem.action && originalCompIdToInflatedCompIdMap[behaviourItem.action.sourceId]) {
			behaviourItem.action.sourceId = originalCompIdToInflatedCompIdMap[behaviourItem.action.sourceId]
		}

		if (behaviourItem.behavior && originalCompIdToInflatedCompIdMap[behaviourItem.behavior.targetId]) {
			behaviourItem.behavior.targetId = originalCompIdToInflatedCompIdMap[behaviourItem.behavior.targetId]
		}

		return behaviourItem
	}

	const fixConnectionItem = (connectionItem: IConnectionItem) => {
		if (
			connectionItem.type === ConnectionType.ConnectionItem ||
			!(connectionItem as ControllerConnectionItem).controllerId
		) {
			return connectionItem
		}

		return connectionItem
	}

	// Fix behaviors_data
	Object.keys(pageData.behaviors_data).forEach((behaviourId) => {
		const dataItem = pageData.behaviors_data[behaviourId] as ObsoleteBehaviorsList

		if (dataItem.type === 'ObsoleteBehaviorsList') {
			const itemsString = (dataItem as ObsoleteBehaviorsList).items
			try {
				const itemsList = JSON.parse(itemsString) as Array<ActionBehavior>
				dataItem.items = JSON.stringify(itemsList.map(fixBehaviourItem))
				pageData.behaviors_data[behaviourId] = dataItem
			} catch (e) {
				console.error('Cannot stringify ObsoleteBehaviorsList', e)
			}
		}
	})

	Object.keys(pageData.connections_data).forEach((behaviourId) => {
		const dataItem = pageData.connections_data[behaviourId] as ConnectionList

		if (dataItem.type === 'ConnectionList') {
			dataItem.items = dataItem.items.map(fixConnectionItem)
		}
	})

	return pageData
}
export {
	resolveInternalRefs,
	inflateRef,
	getUniqueId,
	getUniqueDataQuery,
	mergeData,
	mergeInflatedData,
	fixDataAfterInflate,
	REF_DELIMITER,
	buildWidgetUrl,
}
